import { FC, ReactNode, useMemo } from "react";
import { useIntl } from "react-intl";
import { FixedSizeList, ListChildComponentProps } from "react-window";

import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import { Box, ButtonBase, Popover, Tooltip, Typography } from "@mui/material";

import { formatTrendTooltipDate } from "../../../../../../utils";
import { formatMeasurementLabel } from "../../../../../../utils/measurement-label";
import type { IMultiAnalysisDataMark } from "../../types";
import { MarkTooltip } from "./data-mark-tooltip";

interface IDataMarkGroupPickerProps {
  marks: IMultiAnalysisDataMark[];
  selectedMarkKeys: ReadonlySet<string>;
  anchorPosition: { left: number; top: number } | null;
  selectionHintId?: string;
  onClose: () => void;
  onMarkClick: (mark: IMultiAnalysisDataMark, event: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }) => void;
  renderMarkTooltip?: (mark: IMultiAnalysisDataMark) => ReactNode | null;
}

type IDataMarkRows = Pick<
  IDataMarkGroupPickerProps,
  "marks" | "selectedMarkKeys" | "onMarkClick" | "renderMarkTooltip"
>;

const ROW_HEIGHT = 64;

const DataMarkRow = ({ data, index, style }: ListChildComponentProps<IDataMarkRows>) => {
  const mark = data.marks[index];
  const selected = data.selectedMarkKeys.has(mark.key);

  return (
    <Box style={style}>
      <Tooltip
        title={<MarkTooltip mark={mark} renderMarkTooltip={data.renderMarkTooltip} />}
        enterDelay={400}
        disableInteractive
        componentsProps={{ tooltip: { sx: { bgcolor: "common.white", boxShadow: 3 } } }}
      >
        <ButtonBase
          aria-pressed={selected}
          onClick={event => {
            event.stopPropagation();
            data.onMarkClick(mark, { shiftKey: event.shiftKey, ctrlKey: event.ctrlKey, metaKey: event.metaKey });
          }}
          sx={{
            width: "100%",
            height: "100%",
            px: 1.5,
            gap: 1,
            justifyContent: "flex-start",
            textAlign: "left",
            bgcolor: selected ? "action.selected" : undefined,
            "&:hover": { bgcolor: "action.hover" },
            "&.Mui-focusVisible": { bgcolor: "action.focus" },
          }}
        >
          {selected ? <CheckBox fontSize="small" color="primary" /> : <CheckBoxOutlineBlank fontSize="small" />}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontSize: 13, lineHeight: "18px", fontWeight: 600 }}>
              {mark.pointName}
            </Typography>
            <Typography noWrap sx={{ fontSize: 12, lineHeight: "16px" }}>
              {formatMeasurementLabel(mark.measureName, mark.measureDescription)}
            </Typography>
            <Typography noWrap color="text.secondary" sx={{ fontSize: 11, lineHeight: "16px" }}>
              {formatTrendTooltipDate(mark.date)}
            </Typography>
          </Box>
        </ButtonBase>
      </Tooltip>
    </Box>
  );
};

const getMarkKey = (index: number, data: IDataMarkRows): string => data.marks[index].key;

export const DataMarkGroupPicker: FC<IDataMarkGroupPickerProps> = ({
  marks,
  selectedMarkKeys,
  anchorPosition,
  selectionHintId = "pages.multiAnalysis.timePanel.selectDataMarks",
  onClose,
  onMarkClick,
  renderMarkTooltip,
}) => {
  const { formatMessage } = useIntl();
  const itemData = useMemo(
    () => ({ marks, selectedMarkKeys, onMarkClick, renderMarkTooltip }),
    [marks, selectedMarkKeys, onMarkClick, renderMarkTooltip],
  );
  const title = formatMessage({ id: "pages.multiAnalysis.timePanel.dataMarks" }, { count: marks.length });
  const availableHeight = typeof window === "undefined" ? 384 : Math.max(ROW_HEIGHT, window.innerHeight - 128);
  const width = typeof window === "undefined" ? 420 : Math.min(420, Math.max(180, window.innerWidth - 32));

  return (
    <Popover
      open={anchorPosition !== null && marks.length > 0}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? undefined}
      onClose={onClose}
      PaperProps={{ role: "dialog", "aria-label": title, sx: { userSelect: "none" } }}
    >
      <Box sx={{ width, boxSizing: "border-box", px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{title}</Typography>
        <Typography color="text.secondary" sx={{ fontSize: 11 }}>
          {formatMessage({ id: selectionHintId })}
        </Typography>
      </Box>
      <FixedSizeList
        height={Math.min(marks.length * ROW_HEIGHT, 384, availableHeight)}
        width={width}
        itemCount={marks.length}
        itemSize={ROW_HEIGHT}
        itemData={itemData}
        itemKey={getMarkKey}
        overscanCount={3}
      >
        {DataMarkRow}
      </FixedSizeList>
    </Popover>
  );
};
